// Copyright (c) Microsoft Corporation. All rights reserved.
import { logitechAppId, razerAppId } from '../constants';
import { Trilean } from '../typings';
import { isGamepadConnected } from '../utils';
import { MouseConnectedStatusChangedEvent } from './events';
import { GamepadConnectedStatusChangedEvent } from './events/GamepadConnectedStatusChangedEvent';
import { LowBandwidthStatusChangedEvent } from './events/LowBandwidthStatusChangedEvent';
import { OnlineStatusChangedEvent } from './events/OnlineStatusChangedEvent';
import { StreamReadinessStatusChangedEvent } from './events/StreamReadinessStatusChangedEvent';
const isClient = typeof window !== 'undefined';
export class DeviceCapabilities {
    get online() {
        return this._online;
    }
    get gamepadConnected() {
        return this._gamepadConnected;
    }
    get mouseConnected() {
        return this._mouseConnected;
    }
    get lowBandwidth() {
        return this._lowBandwidth;
    }
    get streamReadiness() {
        return this._streamReadiness;
    }
    get allowNonstandardGamepad() {
        return this._allowNonstandardGamepad;
    }
    constructor(options) {
        var _a, _b, _c, _d, _e;
        this.options = options;
        this.eventTarget = new EventTarget();
        this.gamepadSupported = this.isGamepadSupported();
        this.webrtcSupported = this.isWebRTCSupported();
        this.audioSupported = this.isAudioSupported();
        this.userAgentBlocked = this.isUserAgentBlocked((_a = this.options) === null || _a === void 0 ? void 0 : _a.parser);
        this.userAgentSupported = this.isUserAgentSupported((_b = this.options) === null || _b === void 0 ? void 0 : _b.parser);
        this.keyboardSupported = this.isKeyboardSupported();
        this.mouseSupported = this.isMouseSupported();
        this.fullscreenSupported = this.isFullscreenSupported();
        this._online = (_d = (_c = globalThis === null || globalThis === void 0 ? void 0 : globalThis.navigator) === null || _c === void 0 ? void 0 : _c.onLine) !== null && _d !== void 0 ? _d : true;
        this._allowNonstandardGamepad = false;
        this._gamepadConnected = isGamepadConnected(this._allowNonstandardGamepad);
        this._mouseConnected = false;
        this._lowBandwidth = this.isLowBandwidthConnection();
        this._streamReadiness = this.calculateStreamReadinessStatus();
        // browserSupported is calculated by the following:
        // 1. Make sure all required streaming features are supported (gamepad, webrtc, audio)
        // 2. Make sure the user agent is not blocked
        // 3. Make sure the user agent is officially supported
        // 4. Make sure the browser is not in XboxOS
        // This value is different from streamReadiness because streamReadiness takes into account
        // things like is a controller connected, is the user online, etc. This is meant to be used
        // to determine if we show the BrowserSupportBanner.
        // We only need to calculate this at construction time because all of the values used in
        // the calculation are static and will not change during the lifetime of the instance.
        this.browserSupported =
            this.gamepadSupported &&
                this.webrtcSupported &&
                this.audioSupported &&
                !this.userAgentBlocked &&
                this.userAgentSupported &&
                ((_e = this.options) === null || _e === void 0 ? void 0 : _e.isXboxOS) !== Trilean.YES;
        // setup listeners
        this.cleanupEventListeners = this.setupListeners();
    }
    destroy() {
        var _a;
        // remove listeners
        (_a = this.cleanupEventListeners) === null || _a === void 0 ? void 0 : _a.call(this);
    }
    addEventListener(type, listener, options) {
        this.eventTarget.addEventListener(type, listener, options);
    }
    removeEventListener(type, listener, options) {
        this.eventTarget.removeEventListener(type, listener, options);
    }
    setAllowNonStandardGamepad(value) {
        if (this._allowNonstandardGamepad === value) {
            return;
        }
        this._allowNonstandardGamepad = value;
        // We need to recompute the gamepad connected status every time we change
        // this property because we might have a nonstandard gamepad connected
        // so when we enable support we want the app to know we have a supported controller
        // and tangentially if we we disable the flag we want the app to say that we do not
        // have any gamepad connected because no standard controllers are connected
        this.setGamepadConnected(isGamepadConnected(value));
    }
    setOnline(value) {
        if (this._online !== value) {
            this._online = value;
            this.eventTarget.dispatchEvent(new OnlineStatusChangedEvent());
        }
    }
    setGamepadConnected(value) {
        if (this._gamepadConnected !== value) {
            this._gamepadConnected = value;
            this.eventTarget.dispatchEvent(new GamepadConnectedStatusChangedEvent());
        }
    }
    setMouseConnected(value) {
        if (this._mouseConnected !== value) {
            this._mouseConnected = value;
            this.eventTarget.dispatchEvent(new MouseConnectedStatusChangedEvent());
        }
    }
    setLowBandwidth(value) {
        if (this._lowBandwidth !== value) {
            this._lowBandwidth = value;
            this.eventTarget.dispatchEvent(new LowBandwidthStatusChangedEvent());
        }
    }
    setStreamReadiness(value) {
        if (this._streamReadiness !== value) {
            this._streamReadiness = value;
            this.eventTarget.dispatchEvent(new StreamReadinessStatusChangedEvent());
        }
    }
    setupListeners() {
        if (!isClient) {
            return;
        }
        const onlineStatusChanged = () => {
            this.setOnline(globalThis === null || globalThis === void 0 ? void 0 : globalThis.navigator.onLine);
            // recalculate lowBandwidth whenever online status changes
            this.setLowBandwidth(this.isLowBandwidthConnection());
            this.setStreamReadiness(this.calculateStreamReadinessStatus());
        };
        const gamepadConnectedChanged = () => {
            this.setGamepadConnected(isGamepadConnected(this._allowNonstandardGamepad));
            this.setStreamReadiness(this.calculateStreamReadinessStatus());
        };
        const mouseConnectedChanged = (evt) => {
            this.setMouseConnected(evt.matches);
        };
        globalThis.addEventListener('online', onlineStatusChanged);
        globalThis.addEventListener('gamepadconnected', gamepadConnectedChanged);
        globalThis.addEventListener('gamepaddisconnected', gamepadConnectedChanged);
        const mouseMediaQuery = globalThis.matchMedia('(pointer: fine)');
        this.setMouseConnected(mouseMediaQuery.matches);
        if (mouseMediaQuery.addEventListener) {
            mouseMediaQuery.addEventListener('change', mouseConnectedChanged);
        }
        return () => {
            globalThis.removeEventListener('online', onlineStatusChanged);
            globalThis.removeEventListener('gamepadconnected', gamepadConnectedChanged);
            globalThis.removeEventListener('gamepaddisconnected', gamepadConnectedChanged);
            if (mouseMediaQuery.removeEventListener) {
                mouseMediaQuery.removeEventListener('change', mouseConnectedChanged);
            }
        };
    }
    /**
     * This was originally implemented using the Network Information APIs (wicg.github.io/netinfo/#-dfn-networkinformation-dfn-interface).
     * Unfortunately, those APIs aren't supported on Safari or Firefox as of this writing (12/22/2022). Furthermore,
     * the Chromium implementation does *not* provide reliable connectivity data, both because it's still only partially
     * supported (e.g., bugs.chromium.org/p/chromium/issues/detail?id=775977) and because of concerns around privacy
     * (e.g., device fingerprinting - github.com/WICG/netinfo/issues/64). For a single device within a good (wired) network, the
     * RTT and downlink properties can vary wildly (between 0 and 200 ms for RTT, 0 and 10 Mbps for downlink), which makes sense
     * since these are measured based on HTTP GET times for arbitrary websites visited during the user's browsing session. This isn't
     * good enough for our needs and has led to frequent false-positives that cause user confusion (e.g., reddit.com/r/xcloud/comments/zsfj4y/network_unsupported_lol_what/),
     * so we (xCloud Streaming Team) have disabled this logic for the time being. Right now, the user is better off launching the stream
     * and trusting the server-side rate controller to make the right compromises between latency and video quality.
     */
    isLowBandwidthConnection() {
        // Design and implementation of the Network Test, Network Troubleshooter, and Network Quality Indicator experiences
        // tracked by: https://microsoft.visualstudio.com/Xbox/_workitems/edit/36474167
        return false;
    }
    /**
     * Checks for browser Gamepad API support
     * https://developer.mozilla.org/en-US/docs/Web/API/Gamepad_API
     */
    isGamepadSupported() {
        // If on server, assume gamepad is supported
        return isClient ? !!navigator.getGamepads : true;
    }
    /**
     * Checks if the browser supports necessary WebRTC APIs
     * https://developer.mozilla.org/en-US/docs/Web/API/WebRTC_API
     */
    isWebRTCSupported() {
        // If on server, assume webrtc is supported
        return isClient ? !!globalThis.RTCPeerConnection : true;
    }
    /**
     * Checks for browser audio APIs required to support streaming which relies on the AudioContext API
     * https://developer.mozilla.org/en-US/docs/Web/API/AudioContext
     */
    isAudioSupported() {
        // If on server, assume audio is supported
        return isClient
            ? !!globalThis.AudioContext || !!globalThis.webkitAudioContext
            : true;
    }
    /**
     * These browsers have been blocklisted and should not start a stream because of an issue we have identified that will result in a poor streaming experience
     */
    isUserAgentBlocked(parser) {
        return !!(parser === null || parser === void 0 ? void 0 : parser.satisfies({
            desktop: {
                macos: {
                    safari: '<=11',
                },
            },
        }));
    }
    /**
     * These are the set of browsers we have greenlit for game streaming, they are known to have the features required for a decent streaming experience.
     * Browsers outside this list will throw a warning but will still be allowed to start a stream.
     */
    isUserAgentSupported(parser) {
        var _a, _b;
        // Identify native application wrappers and explicitly state they're supported
        const userAgent = parser === null || parser === void 0 ? void 0 : parser.getUA();
        if (((_a = this.options) === null || _a === void 0 ? void 0 : _a.isSupportedTVBrowser) ||
            (userAgent === null || userAgent === void 0 ? void 0 : userAgent.includes(logitechAppId)) ||
            (userAgent === null || userAgent === void 0 ? void 0 : userAgent.includes(razerAppId))) {
            return true;
        }
        // The Bowser JS library's "satisfies" method does *NOT* regard Chromium-based browsers as "chrome". Only Google Chrome browser falls under
        // the "chrome" category, which leaves out other Chromium-based browsers (Edge, Opera, Brave, Silk, UCBrowser, etc) that would also work fine.
        // Thus, we need the extra `isSupportedChromiumBasedBrowser` property that gets calculated based on the browser engine.
        // See: https://github.com/lancedikson/bowser/issues/450
        return (((_b = this.options) === null || _b === void 0 ? void 0 : _b.isSupportedChromiumBasedBrowser) ||
            !!(parser === null || parser === void 0 ? void 0 : parser.satisfies({
                desktop: {
                    chrome: '>=79',
                    edge: '>=79',
                    macos: {
                        safari: '>=14',
                    },
                },
                mobile: {
                    ios: {
                        safari: '>=14',
                    },
                    android: {
                        chrome: '>=79',
                        edge: '>=79',
                    },
                },
                tablet: {
                    ios: {
                        safari: '>=14',
                    },
                    android: {
                        chrome: '>=79',
                        edge: '>=79',
                    },
                },
            })));
    }
    /**
     * Checks for browser Keyboard API support
     * https://developer.mozilla.org/en-US/docs/web/api/keyboard_api
     */
    isKeyboardSupported() {
        // If on server, assume keyboard is supported
        return isClient ? !!navigator.keyboard : true;
    }
    /**
     * Checks for browser Pointer Lock API support
     * https://developer.mozilla.org/en-US/docs/Web/API/Pointer_Lock_API
     */
    isMouseSupported() {
        // If on server, assume mouse is supported
        return isClient ? !!document.exitPointerLock : true;
    }
    isFullscreenSupported() {
        // If on server, assume fullscreen is supported
        return isClient
            ? document.fullscreenEnabled || !!document.webkitFullscreenEnabled
            : true;
    }
    calculateStreamReadinessStatus() {
        var _a, _b, _c;
        const areRequiredStreamingFeaturesSupported = this.gamepadSupported &&
            this.webrtcSupported &&
            this.audioSupported;
        // Users cannot start a stream at all so their browser is blocked
        if (!areRequiredStreamingFeaturesSupported || this.userAgentBlocked) {
            return 'blocked';
        }
        // Refers to a user being on iOS but not using Safari (using Chrome, etc)
        const partialIOSSupport = ((_a = this.options) === null || _a === void 0 ? void 0 : _a.isIOS) && !((_b = this.options) === null || _b === void 0 ? void 0 : _b.isSafari);
        // We determine if the user is partially ready by checking the following:
        // 1 - If they are not online, they are not ready
        // 2 - If they have low bandwidth, they are not ready
        // 3 - If they are on iOS and not using Safari, they are not ready
        // 4 - If they are on Xbox OS, they are not ready
        // 5 - If their user agent isn't explicity supported, they are not ready
        if (!this.online ||
            this.lowBandwidth ||
            partialIOSSupport ||
            ((_c = this.options) === null || _c === void 0 ? void 0 : _c.isXboxOS) ||
            !this.userAgentSupported) {
            return 'partially-ready';
        }
        return 'ready';
    }
}
//# sourceMappingURL=DeviceCapabilities.js.map